import React, { useState } from 'react'
import { Form, Input, Result } from 'antd'
import type { ResultStatus } from 'apollo-gql'
import { useTranslation } from 'lib/i18n'

import CenteredForm from 'components/CenteredForm/CenteredForm'
import CtaButton from 'components/CtaButton/CtaButton'
import useSendMagicLinkEmail from 'hooks/useSendMagicLinkEmail'

import styles from './MagicLinkForm.module.css'

type MagicLinkFormProps = {
  emailPrefill: string
}

type ErrorFieldsProps = {
  errorFields: { name: (string | number)[] }[]
}

const MagicLinkForm: React.FC<MagicLinkFormProps> = ({ emailPrefill }) => {
  const { t } = useTranslation()
  const [result, setResult] = useState<ResultStatus | boolean>(null)
  const [formEmail, setFormEmail] = useState(emailPrefill)

  const [sendMagicLinkEmail, { loading }] = useSendMagicLinkEmail({
    onCompleted: (data) => {
      setResult(data.sendMagicLinkEmail.result)
    },
  })

  const [form] = Form.useForm()

  const onFinishFailed = ({ errorFields }: ErrorFieldsProps) => {
    form.scrollToField(errorFields[0].name)
  }

  const handleFinish = ({ email }: { email: string }) => {
    setFormEmail(email)
    sendMagicLinkEmail({ email })
  }

  if (result === 'SUCCESS') {
    return (
      <CenteredForm>
        <Result
          status="success"
          title={t('MagicLinkForm.successTitle', 'Check your email')}
          subTitle={t(
            'MagicLinkForm.successSubTitle',
            'Open your email sent to {{email}} and click the button in the email to sign in.',
            { email: formEmail },
          )}
        />
        <a
          className={styles.retryLink}
          onClick={() => setResult(false)}
          role="button"
          tabIndex={0}
        >
          {t(
            'MagicLinkForm.retryLink',
            "Didn't receive an email or entered the wrong email? Click here to try again.",
          )}
        </a>
      </CenteredForm>
    )
  }

  if (result === 'ERROR') {
    return (
      <CenteredForm>
        <Result
          status="error"
          title={t('MagicLinkForm.errorTitle', 'Something went wrong')}
          subTitle={t(
            'MagicLinkForm.errorSubTitle',
            'If you have trouble signing in, please contact us at support@memmo.me',
          )}
        />
      </CenteredForm>
    )
  }

  return (
    <CenteredForm>
      <Form
        onFinish={handleFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
      >
        <p>
          {t(
            'MagicLinkForm.formDescription',
            "Enter your email adress and we'll send a magic link to sign you in password-free.",
          )}
        </p>

        <Form.Item
          name="email"
          label={t('MagicLinkForm.emailLabel', 'Email')}
          rules={[{ required: true }]}
          initialValue={formEmail}
        >
          <Input type="email" />
        </Form.Item>

        <Form.Item>
          <CtaButton htmlType="submit" block type="primary" loading={loading}>
            {t('MagicLinkForm.sendLinkButton', 'Sign in with email')}
          </CtaButton>
        </Form.Item>
      </Form>
    </CenteredForm>
  )
}

export default MagicLinkForm
